import { useState, type ReactNode } from 'react';
import { ACCENT, BORDER, FONT_MONO, SURFACE, TEXT_SECONDARY } from '../../tokens';

interface InfoToggleProps {
  title: string;
  children: ReactNode;
  /** Fired on hover so the calculator can glow the stat this explains. */
  onHover?: () => void;
  onLeave?: () => void;
}

/** Small "how this works" expander above each calculator slider. */
export function InfoToggle({ title, children, onHover, onLeave }: InfoToggleProps) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ marginBottom: 10 }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          fontSize: '0.65rem',
          fontFamily: FONT_MONO,
          color: ACCENT,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          lineHeight: 1.4,
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke={ACCENT}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        {title}
        <span
          aria-hidden="true"
          style={{
            marginLeft: 2,
            display: 'inline-block',
            transform: open ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.25s',
            fontSize: '1rem',
          }}
        >
          &#9662;
        </span>
      </button>

      {open && (
        <div
          style={{
            fontSize: '0.75rem',
            color: TEXT_SECONDARY,
            lineHeight: 1.6,
            marginTop: 8,
            padding: '10px 14px',
            background: SURFACE,
            borderRadius: 10,
            border: `1px solid ${BORDER}`,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}
